import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { useEffect, useState, useCallback } from 'react'

import { IconButton } from '~/ui'
import { cn } from '~/utils/classnames'

export type TextCarouselItem = {
  title: string
  subtitle: string
}

type TextCarouselProps = {
  items: TextCarouselItem[]
  className?: string
  autoPlayInterval?: number
  showControls?: boolean
}

const SLIDE_DURATION = 5000

export const TextCarousel = ({
  items,
  className,
  autoPlayInterval = SLIDE_DURATION,
  showControls = true,
}: TextCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [progress, setProgress] = useState(0)

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1))
    setProgress(0)
  }, [items.length])

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1))
    setProgress(0)
  }, [items.length])

  useEffect(() => {
    const timeout = setTimeout(goToNext, autoPlayInterval)

    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 10, autoPlayInterval))
    }, 10)

    return () => {
      clearTimeout(timeout)
      clearInterval(progressInterval)
    }
  }, [currentIndex, autoPlayInterval, goToNext])

  const textVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  }

  return (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      <div className="relative min-h-[100px] w-full text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            variants={textVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="flex flex-col gap-2"
          >
            <h2 className="text-highlight text-2xl font-semibold md:text-3xl">
              {items[currentIndex]?.title}
            </h2>
            <p className="text-highlight/64 text-base md:text-lg">
              {items[currentIndex]?.subtitle}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-3">
        {showControls && (
          <IconButton
            variant="ghost"
            size="sm"
            onClick={goToPrev}
            aria-label="Previous slide"
            className="text-highlight/64 hover:text-highlight"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </IconButton>
        )}

        <div className="flex items-center gap-2">
          {items.map((_, index) => {
            const isActive = index === currentIndex
            const progressWidth = isActive
              ? (progress / autoPlayInterval) * 100
              : 0

            return (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index)
                  setProgress(0)
                }}
                className={cn(
                  'relative h-1 overflow-hidden rounded-full transition-all duration-300',
                  isActive ? 'bg-highlight/32 w-10' : 'bg-highlight/32 w-2'
                )}
                aria-label={`Go to slide ${index + 1}`}
              >
                {isActive && (
                  <div
                    className="bg-highlight absolute top-0 left-0 h-full rounded-full transition-[width] duration-100 ease-linear"
                    style={{ width: `${progressWidth}%` }}
                  />
                )}
              </button>
            )
          })}
        </div>

        {showControls && (
          <IconButton
            variant="ghost"
            size="sm"
            onClick={goToNext}
            aria-label="Next slide"
            className="text-highlight/64 hover:text-highlight"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </IconButton>
        )}
      </div>
    </div>
  )
}

export default TextCarousel
